/**
 * `wayai records …` — the ontology's row entity. A top-level namespace because
 * WayAI owns no `records` concept of its own.
 */

import { Command } from 'commander';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import {
  foreignSegment,
  historyQuery,
  outputFormat,
  parseData,
  pathSegment,
  requireBase,
} from '../helpers.js';
import { confirm } from '../../lib/utils.js';

/**
 * A record's `<id>` argument, encoded for the path it lands in.
 *
 * With `--external-source` the argument is an id ANOTHER system minted, so the
 * slug grammar would reject legitimate values; without it, the argument is an
 * id this platform issued and the stricter grammar applies.
 */
function recordKey(id: string, externalSource: string | undefined): string {
  return externalSource ? foreignSegment(id, 'external id') : pathSegment(id, 'record id');
}

export function buildRecordsCommand(): Command {
  const records = new Command('records').description('Manage records');

  records
    .command('upsert <record_type>')
    .description('Create or update a record')
    .requiredOption('--data <json>', 'Record data (inline JSON or @filename)')
    .option('--id <id>', 'Internal record ID (UUID) to update a known record')
    .option('--external-id <id>', 'External/agent-supplied ID for idempotent upsert')
    .option('--external-source <source>', 'Source system for external_id (e.g. stripe)')
    .action(async function (
      this: Command,
      recordType: string,
      opts: { data: string; id?: string; externalId?: string; externalSource?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const body: Record<string, unknown> = { data: parseData(opts.data) };
      if (opts.externalId) body.external_id = opts.externalId;
      if (opts.externalSource) body.external_source = opts.externalSource;
      const path = opts.id
        ? `/v1/${base}/records/${pathSegment(recordType, 'record_type')}/${pathSegment(opts.id, '--id')}`
        : `/v1/${base}/records/${pathSegment(recordType, 'record_type')}`;
      printOutput(await client.request('PUT', path, body), outputFormat(this));
    });

  records
    .command('query <record_type>')
    .description('List/search records: exact filters + fuzzy `search`, sorting, pagination')
    .option(
      '--external-source <source>',
      'List from an external source (proxy-backed record_type). Combine with --filter when the source forwards filters.',
    )
    .option(
      '--filter <json>',
      'Filter DSL expression (inline JSON or @filename). Use the `search` operator for fuzzy matching.',
    )
    .option(
      '--sort <json>',
      'Sort expressions (inline JSON or @filename), e.g. [{"field":"data.created","direction":"desc"}]',
    )
    .option('--fields <list>', 'Comma-separated field projection')
    .option('--limit <n>', 'Max results')
    .option('--offset <n>', 'Pagination offset')
    .action(async function (
      this: Command,
      recordType: string,
      opts: {
        externalSource?: string;
        filter?: string;
        sort?: string;
        fields?: string;
        limit?: string;
        offset?: string;
      },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const qs = new URLSearchParams();
      if (opts.externalSource) qs.set('external_source', opts.externalSource);
      if (opts.filter) qs.set('filter', JSON.stringify(parseData(opts.filter)));
      if (opts.sort) qs.set('sort', JSON.stringify(parseData(opts.sort)));
      if (opts.fields) qs.set('fields', opts.fields);
      if (opts.limit) qs.set('limit', opts.limit);
      if (opts.offset) qs.set('offset', opts.offset);
      const q = qs.toString();
      printOutput(
        await client.request('GET', `/v1/${base}/records/${pathSegment(recordType, 'record_type')}${q ? `?${q}` : ''}`),
        outputFormat(this),
      );
    });

  records
    .command('get <record_type> <id>')
    .description(
      'Get a record by internal ID, or by external_id (pass --external-source to scope it)',
    )
    .option(
      '--external-source <source>',
      'Treat <id> as an external_id scoped to this source system (e.g. stripe)',
    )
    .action(async function (
      this: Command,
      recordType: string,
      id: string,
      opts: { externalSource?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      // <id> may be an internal UUID or an external_id; encode it for the path segment.
      let path = `/v1/${base}/records/${pathSegment(recordType, 'record_type')}/${recordKey(id, opts.externalSource)}`;
      if (opts.externalSource) path += `?external_source=${encodeURIComponent(opts.externalSource)}`;
      printOutput(await client.request('GET', path), outputFormat(this));
    });

  records
    .command('delete <record_type> <id>')
    .description(
      'Delete a record by internal ID, or by external_id (pass --external-source to scope it)',
    )
    .option('-y, --yes', 'Skip confirmation prompt')
    .option(
      '--external-source <source>',
      'Treat <id> as an external_id scoped to this source system (e.g. stripe)',
    )
    .action(async function (
      this: Command,
      recordType: string,
      id: string,
      opts: { yes?: boolean; externalSource?: string },
    ) {
      if (
        !opts.yes &&
        !(await confirm(`Delete record ${recordType}/${id}? This cannot be undone.`))
      ) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      let path = `/v1/${base}/records/${pathSegment(recordType, 'record_type')}/${recordKey(id, opts.externalSource)}`;
      // With a source, <id> is an external_id: forward both query params (scope +
      // external_id) so the backend resolves the native record by its external
      // identity rather than treating the segment as an internal UUID.
      if (opts.externalSource) {
        path += `?external_source=${encodeURIComponent(opts.externalSource)}&external_id=${encodeURIComponent(id)}`;
      }
      await client.request('DELETE', path);
      console.log('Deleted');
    });

  records
    .command('history <id>')
    .description(
      'View the change history (audit log) for a record — every version, who changed it, and when',
    )
    .option('--limit <n>', 'Max results')
    .option('--offset <n>', 'Pagination offset')
    .option('--diff', 'Include field-level diffs between versions')
    .action(async function (
      this: Command,
      id: string,
      opts: { limit?: string; offset?: string; diff?: boolean },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('GET', `/v1/${base}/audit/records/${pathSegment(id, 'record id')}${historyQuery(opts)}`),
        outputFormat(this),
      );
    });

  return records;
}
